import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react"

import { Box, ButtonBase, Stack, Typography, type ButtonBaseProps } from "@mui/material"
import { alpha, styled } from "@mui/material/styles"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"

import type { FC, FCWC, TripTeaserNewProps } from ".."
import { Button, Carousel, ContainerGrid, Grid, SvgIcon, TripTeaserNew } from ".."
import type { LinkType } from "../../graphql/types"

type SuggestedTripsProps = {
  id?: string
  title: string
  subtitle: string
  items: TripTeaserNewProps[]
  link?: LinkType
  onLike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  onUnlike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
}

const SuggestedTrips: FC<SuggestedTripsProps> = ({
  id = "suggested-trips",
  title,
  subtitle,
  items,
  link,
  onLike,
  onUnlike,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
  })

  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [trackWidth, setTrackWidth] = useState(1600)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      setCanScrollPrev(emblaApi.canScrollPrev())
      setCanScrollNext(emblaApi.canScrollNext())

      if (scrollTrackRef.current) {
        const { clientWidth: trackSize } = scrollTrackRef.current
        const emblaClientWidth = emblaApi.containerNode().clientWidth
        const emblaScrollWidth = emblaApi.containerNode().scrollWidth
        setThumbWidth(Math.max((emblaClientWidth / emblaScrollWidth) * trackSize, 20))
        setTrackWidth(trackSize)
      }
    },
    [scrollTrackRef]
  )

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [])

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const snaps = emblaApi.scrollSnapList()
      setScrollSnaps(snaps)
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const handleTrackClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef

      if (!emblaApi || !trackCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("scroll", onScroll)
    emblaApi.on("select", onSelect)
  }, [emblaApi, scrollTrackRef, onInit, onScroll])

  return (
    <ContainerGrid id={id} py={{ xs: 18, lg: 24 }} rowGap={{ xs: 0 }}>
      <SuggestedTripsHeader title={title} subtitle={subtitle} />

      <Stack height="100%" alignItems="center" justifyContent="center">
        {canScrollPrev && (
          <Box
            component={CarouselBtn}
            display={{ xs: "none", md: "block" }}
            padding={5}
            sx={(theme) => ({
              transform: "translateY(-50%)",
              pointerEvents: "all",
              background: theme.palette["neutral.100"].main,
              borderRadius: theme.spacing(25),
              boxShadow: "2px 2px 8px 0px rgba(0, 0, 0, 0.08)",
              border: `1px solid ${theme.palette["neutral.200"].main}`,
            })}
            onClick={() => scrollPrev()}
          >
            <SvgIcon
              variant="arrow"
              size={20}
              sx={{
                fill: (theme) => theme.palette["neutral.700"].main,
                mt: 0.5,
                background: (theme) => theme.palette["neutral.100"].main,
                borderRadius: (theme) => theme.spacing(25),
                transform: (theme) =>
                  theme.transitions.create("fill", {
                    duration: theme.transitions.duration.standard,
                  }),
              }}
            />
          </Box>
        )}
      </Stack>
      <Box gridColumn={{ xs: "1 / -1", lg: "2 / -2" }}>
        <Carousel.Root
          carouselRef={emblaRef}
          display="grid"
          pb={5}
          columnGap={{ xs: 3, md: 8 }}
          gridAutoFlow="column"
          gridAutoColumns={{
            xs: "minmax(95%, auto)",
            md: "minmax(387px, auto)",
            xl: "minmax(calc(33.3% - (32px * 2 / 3)), auto)",
          }}
          containerProps={{ px: 2, mx: -2 }}
          viewportProps={{ px: 0 }}
        >
          {items?.map((data, i) => (
            <TripTeaserNew key={i} {...data} isSpecial={false}/>
          ))}
        </Carousel.Root>

        {thumbWidth !== trackWidth && (
          <Box height="2px" overflow="hidden" mt={{ xs: 5, md: 10 }} position="relative">
            <Box
              ref={scrollTrackRef}
              onClick={(e) => handleTrackClick(e)}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["neutral.200"].main}
              sx={{ "&:hover": { cursor: "pointer" } }}
            />
            <Box
              ref={scrollThumbRef}
              position="absolute"
              width={thumbWidth}
              top={0}
              left={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["primary.800"].main}
            />
          </Box>
        )}
      </Box>
      <Stack height="100%" alignItems="center" justifyContent="center">
        {canScrollNext && (
          <Box
            component={CarouselBtn}
            display={{ xs: "none", lg: "block" }}
            padding={5}
            sx={(theme) => ({
              transform: "translateY(-50%)",
              pointerEvents: "all",
              background: theme.palette["neutral.100"].main,
              borderRadius: theme.spacing(25),
              boxShadow: "2px 2px 8px 0px rgba(0, 0, 0, 0.08)",
              border: `1px solid ${theme.palette["neutral.200"].main}`,
            })}
            onClick={() => scrollNext()}
          >
            <SvgIcon
              variant="arrow"
              size={20}
              sx={{
                fill: (theme) => theme.palette["neutral.700"].main,
                background: (theme) => theme.palette["neutral.100"].main,
                mt: 0.5,
                rotate: "180deg",
                transform: (theme) =>
                  theme.transitions.create("fill", {
                    duration: theme.transitions.duration.standard,
                  }),
              }}
            />
          </Box>
        )}
      </Stack>
      <Grid gridColumn="1 / -1">
        <Box gridColumn={{ xs: "1 / -1", lg: "1 / -" }}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent={{ xs: "center", md: "space-between" }}
            alignItems={{ xs: "center", md: "center" }}
            gap={10}
            mb={{ xs: 2, md: 2 }}
          >
            <Stack
              width="100%"
              rowGap={5}
              position="relative"
              textAlign={{ xs: "center", md: "center" }}
            >
              {link && <SuggestedTripsCTA link={link} />}
            </Stack>
          </Box>
        </Box>
      </Grid>
    </ContainerGrid>
  )
}

export default SuggestedTrips

/** A sub-component for the header. */
const SuggestedTripsHeader: FC<Omit<SuggestedTripsProps, "items">> = ({ title, subtitle }) => {
  return (
    <Grid gridColumn="1 / -1">
      <Box gridColumn={{ xs: "1 / -1", lg: "2 / -2" }}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "center", md: "space-between" }}
          alignItems={{ xs: "center", md: "center" }}
          gap={10}
          mb={{ xs: 6, md: 12 }}
        >
          <Stack
            width="100%"
            rowGap={5}
            position="relative"
            textAlign={{ xs: "center", md: "center" }}
            id="suggested-trips-header"
          >
            <SuggestedTripsSubtitle>{subtitle}</SuggestedTripsSubtitle>
            <SuggestedTripsTitle>{title}</SuggestedTripsTitle>
          </Stack>
        </Box>
      </Box>
    </Grid>
  )
}

/** A sub-component for the teaser CTA button. */
const SuggestedTripsCTA: FC<{ link: LinkType }> = ({ link }) => (
  <Box
    display="flex"
    pt={10}
    alignItems="center"
    justifyContent={{ xs: "center", md: "center" }}
    minWidth={200}
    sx={(theme) => ({
      [theme.breakpoints.down("md")]: {
        textAlign: "center",
      },
    })}
  >
    <Button
      sx={(theme) => ({
        width: "100%",
        [theme.breakpoints.up("lg")]: {
          width: "100%",
          margin: "0 128px",
        },
      })}
      variant="tertiary"
      href={link.uri}
    >
      {"View All Packages"}
    </Button>
  </Box>
)

/** A sub-component for the teaser header title. */
const SuggestedTripsTitle: FCWC = ({ children }) => (
  <Typography
    variant="heading.2xl"
    component="h2"
    color={(theme) => theme.palette["neutral.700"].main}
    zIndex={1}
  >
    {children}
  </Typography>
)

/** A sub-component for the teaser header subtitle. */
const SuggestedTripsSubtitle: FCWC = ({ children }) => (
  <Typography
    variant="body.lg"
    component="h3"
    color={(theme) => theme.palette["neutral.700"].main}
    zIndex={1}
  >
    {children}
  </Typography>
)

/** A sub-component for the title in suggested trip */
const TripTitle: FCWC = ({ children }) => (
  <Typography variant="heading.md" component="h2" mb={{ xs: 4, md: 5 }}>
    {children}
  </Typography>
)

/** A sub-component for the descripton in suggested trip */
const TripDescription: FCWC = ({ children }) => (
  <Stack direction="row" columnGap={{ xs: 2, md: 3 }} alignItems="center">
    <SvgIcon
      variant="clock"
      size={{ xs: 12, md: 16 }}
      sx={{ fill: (theme) => theme.palette["primary.800"].main }}
    />
    <Typography variant="body.sm" component="div" fontWeight={500}>
      {children}
    </Typography>
  </Stack>
)

/** A sub-component for the duration in suggested trip */
const TripDuration: FCWC = ({ children }) => (
  <Box
    lineHeight={{ xs: "48px", md: "62px" }}
    pr={5}
    borderRight={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
  >
    <Typography variant="body.sm">{children}</Typography>
  </Box>
)

/** A sub-component for the price in suggested trip */
const TripPrice: FCWC = ({ children }) => <Typography variant="body.sm">{children}</Typography>

/** A sub-component for the savings in suggested trip */
const TripSavings: FCWC = ({ children }) => (
  <Typography
    variant="body.sm"
    fontWeight={500}
    sx={{ color: (theme) => theme.palette["primary.900"].main }}
  >
    {children}
  </Typography>
)

/** A sub-component for the Carousel button */
const CarouselBtn: FCWC<ButtonBaseProps> = ({ children, onClick }) => {
  return (
    <Box
      component={ButtonBase}
      display={{ xs: "none", lg: "block" }}
      sx={(theme) => ({
        "transform": "translateY(-50%)",
        "pointerEvents": "all",
        "background": theme.palette["neutral.100"].main,
        "borderRadius": theme.spacing(25),
        "border": `1px solid ${theme.palette["neutral.200"].main}`,
        "transition": theme.transitions.create("box-shadow"),
        "width": 62,
        "height": 62,

        "&:hover": {
          boxShadow: `2px 2px 8px 0px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
        },
      })}
      onClick={onClick}
    >
      {children}
    </Box>
  )
}

/** styled components */
const SuggestedTripsItemOverlayStyle = styled(Stack)(
  ({ theme }) => `
    top: 0;
    right: ${theme.spacing(1)};
    left: ${theme.spacing(1)};
    padding: ${theme.spacing(3, 2)};
    position: absolute;
    ${theme.breakpoints.up("md")} {
      padding: ${theme.spacing(5)};
    }
  `
)
